import React from "react";
import { Flex } from "rebass/styled-components";
import { playIcon } from "../../../constants/constants";
import {
  ContentSize,
  DynamicButtonContent,
  DynamicCardContent,
  DynamicCardStyle,
  DynamicCardType,
  IconPack
} from "../../models/app-data-model";
import { addTitleToInlineSvg, sanitizeRichText } from "../../services/string-manipulation/string-manipulation-service";
import DynamicButton from "../dynamic-button/dynamic-button";
import { Modal, ModalContent, ModalTrigger } from '../../components/modal/modal';
import Video from "../video/video";
import "./dynamic-card.scss";
import useReferenceUrl, { SimpleReference } from "../../hooks/reference-url/useReferenceUrl";
import ShLink from "../sh-link/sh-link";

export type DynamicCardProps = {
  card_type: DynamicCardType;
  card_width?: number;
  card_style: DynamicCardStyle;
  card_content: DynamicCardContent;
  card_content_size: ContentSize;
  iconPack: IconPack[];
};

const DynamicCard: React.FC<DynamicCardProps> = ({
  card_type,
  card_width,
  card_style,
  card_content,
  card_content_size,
  iconPack
}: DynamicCardProps) => {
  const dynamicButtonContent =
    card_content.cta?.display_text &&
    card_content.cta.reference &&
    ({
      text: card_content?.cta?.display_text,
      icon: "button_arrow",
      reference: card_content?.cta?.reference,
      settings: {
        button_type: "tertiary",
        color: "default",
        size: "extra-large",
        dark_mode: false
      }
    } as DynamicButtonContent);

  const dynamic_card_type = card_type.toLowerCase();
  const content_size = card_content_size.toLocaleLowerCase();
  const cardClasses = `content-card grid-card ${card_width === 4 ? 'quarter-width' : card_width === 3 ? 'third-width' : card_width === 2 ? 'half-width' : 'full-width'
    }`;

  let media, media_video;

  switch (dynamic_card_type) {
    case "image":
      media = card_content.card_image && <img className={`card-media ${card_width === 1 ? "circular" : "square"}`} src={card_content.card_image.url} alt={card_content.image_alt_text} />;
      break;
    case "video":
      media = card_content.card_video && card_content.card_video.length > 0 && (
        // This element will be used to open the Video modal
        <div className={`card-media video ${card_width === 1 ? "circular" : "square"} `} style={{ backgroundImage: `url(${card_content.card_video[0]?.video_poster?.url})` }}>
          <span className="play-icon"></span>
        </div>
      );
      media_video = card_content.card_video && card_content.card_video.length > 0 && (
        <div className="card-video-container">
          <Video
            className="card-video"
            width="100%"
            height="100%"
            isBackground={false}
            controls={true}
            playing={true}
            url={card_content?.card_video[0].url}
            muted={false}
            autoplay={true}
            loop={false}
          ></Video>
        </div>
      )
      break;
    case "icon":
      media = card_content.card_icon && (
        <div className="icon-container">
          <img className="card-icon" src={card_content.card_icon.url} alt={card_content.icon_alt_text} />
        </div>
      );
      break;
    default:
      break;
  }

  const card_structure = (
    <div className={`${card_width === 1 ? "horizontal" : "vertical"} card-structure`}
    >
      {media}
      <div className="card-text-content">
      <div className={`${content_size}-content-size`}>
        <div className="card-header">
          {card_content?.card_headline && <div className="card-headline xl-2">{card_content.card_headline}</div>}
          {card_content?.card_subheadline && <div className="card-subheadline medium-text">{card_content.card_subheadline}</div>}
          {card_content?.card_caption && <div className="card-caption extra-small-text">{card_content.card_caption}</div>}
        </div>
        <div className="card-body large-text" dangerouslySetInnerHTML={{ __html: sanitizeRichText(card_content?.card_body) }} />
      </div>

      {dynamicButtonContent && (
        <div className="cta-container" data-testid="card-button">
          <DynamicButton
            content={dynamicButtonContent}
            iconPack={iconPack}
            nonFocusable={dynamic_card_type === "video" ? false : true}
            onClick={() => {}}
          />
        </div>
      )}
      </div>
      </div>
  );

  const card_item = card_content.cta?.display_text ? (
    <ShLink featureToggles={[]} iconPack={iconPack} className={`${card_style}-dynamic-card ${dynamic_card_type} ${cardClasses} ${card_content.cta?.display_text ? 'has-cta' : ''}`} reference={card_content.cta?.reference}>
      {card_structure}
    </ShLink>
  ) : (
    <div className={`${card_style}-dynamic-card ${dynamic_card_type} ${cardClasses} ${card_content.cta?.display_text ? 'has-cta' : ''}`}>
    {card_structure}
    </div>
  );

  if (dynamic_card_type === "video") {
    return (
      <Modal>
        <ModalTrigger asChild={true}>
          <div tabIndex={0} className={`${card_style}-dynamic-card ${dynamic_card_type} ${cardClasses} ${card_content.cta?.display_text ? 'has-cta' : ''}`}>
            {card_structure}
          </div>
        </ModalTrigger>
        <ModalContent iconPack={iconPack as IconPack[]} videoModal={true}>
          {media_video}
        </ModalContent>
      </Modal>
    );
  }

  return card_item;
};

export default DynamicCard;
